import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface Registration {
  id: string;
  status: string;
  registeredAt: string;
  auction: { id: string; title: string; status: string; startAt: string; endAt: string; currentBid: number | string; coverImage: string | null };
}
interface MyBid {
  id: string;
  amount: number | string;
  createdAt: string;
  auction: { id: string; title: string; status: string; currentBid: number | string; winningBidId: string | null };
}

export function MyAuctionsPage() {
  const { data } = useQuery({
    queryKey: ['me', 'auctions'],
    queryFn: () => api.get<ApiSuccess<{ registrations: Registration[]; bids: MyBid[] }>>('/api/me/auctions'),
  });

  const regs = data?.data?.registrations ?? [];
  const bids = data?.data?.bids ?? [];

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-6">مزاداتي</h1>

      <h2 className="font-black text-lg text-navy-dark mb-3 border-r-4 border-gold pr-3">📋 مزادات سجّلت فيها</h2>
      {regs.length === 0 ? (
        <Card className="mb-6"><CardBody className="text-center py-8 text-gray-500">لم تسجّل في أي مزاد بعد</CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {regs.map((r) => (
            <Link key={r.id} to={`/auctions/${r.auction.id}`}>
              <Card hover>
                <div className="h-32 bg-gradient-to-br from-navy to-navy-dark relative">
                  {r.auction.coverImage ? (
                    <img src={r.auction.coverImage} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white text-4xl">🔨</div>
                  )}
                  <div className="absolute top-2 right-2">
                    <Badge color={r.auction.status === 'LIVE' ? 'red' : r.auction.status === 'SCHEDULED' ? 'navy' : 'gray'}>
                      {r.auction.status === 'LIVE' ? 'مباشر' : r.auction.status === 'SCHEDULED' ? 'قادم' : 'منتهي'}
                    </Badge>
                  </div>
                </div>
                <CardBody>
                  <h3 className="font-black line-clamp-1">{r.auction.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">سجّلت: {new Date(r.registeredAt).toLocaleDateString('ar-SA')}</p>
                  <div className="font-black text-navy-dark mt-2">{Number(r.auction.currentBid).toLocaleString('en-US')} ر.س</div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <h2 className="font-black text-lg text-navy-dark mb-3 border-r-4 border-gold pr-3">💰 مزايداتي</h2>
      {bids.length === 0 ? (
        <Card><CardBody className="text-center py-8 text-gray-500">لم تشارك بأي مزايدة بعد</CardBody></Card>
      ) : (
        <Card>
          <CardBody className="!p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">المزاد</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">مبلغ مزايدتي</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">السعر الحالي</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">الحالة</th>
                  <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">التاريخ</th>
                </tr>
              </thead>
              <tbody>
                {bids.map((b) => {
                  const isWinning = b.auction.winningBidId === b.id;
                  return (
                    <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3"><Link to={`/auctions/${b.auction.id}`} className="font-bold text-navy-dark hover:text-gold">{b.auction.title}</Link></td>
                      <td className="px-4 py-3 font-black">{Number(b.amount).toLocaleString('en-US')}</td>
                      <td className="px-4 py-3">{Number(b.auction.currentBid).toLocaleString('en-US')}</td>
                      <td className="px-4 py-3">{isWinning ? <Badge color="green">🏆 الأعلى</Badge> : <Badge color="gray">سابقة</Badge>}</td>
                      <td className="px-4 py-3 text-xs">{new Date(b.createdAt).toLocaleString('ar-SA')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
